import type { QueryRef } from '@apollo/client';
import { useBackgroundQuery, useReadQuery } from '@apollo/client';
import type { UseBackgroundQueryResult } from '@apollo/client/react/hooks';
import { Suspense, useMemo, useState, useTransition } from 'react';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { WarningMessageBox } from '@xing-com/crate-entity-pages-common';
import { useEntityPageStore } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import type { FilterStateSelectedItemsType } from '@xing-com/crate-entity-pages-jobs';
import { JobsFilter, JobsListItem } from '@xing-com/crate-entity-pages-jobs';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';

import { JobsDetailSkeleton, JobsDetail } from './components';
import type {
  JobsDetailQuery,
  JobsDetailQueryVariables,
} from './graphql/queries/jobs-detail-query.gql-types';
import { JobsDetailDocument } from './graphql/queries/jobs-detail-query.gql-types';
import {
  buildJobsFilterAggregations,
  buildJobsDetailVariables,
  isError,
  LIST_LIMIT,
  mergeJobsDetailResults,
} from './utils/app-helpers';

type ActiveFiltersState = {
  keywords: string;
  selectedItems: FilterStateSelectedItemsType;
};

const DEFAULT_ACTIVE_FILTERS: ActiveFiltersState = {
  keywords: '',
  selectedItems: {
    careerLevels: [],
  },
};

type AppInnerProps = {
  fetchMore: UseBackgroundQueryResult<
    JobsDetailQuery,
    JobsDetailQueryVariables
  >['fetchMore'];
  queryRef: QueryRef<JobsDetailQuery | undefined, JobsDetailQueryVariables>;
  refetch: UseBackgroundQueryResult<
    JobsDetailQuery,
    JobsDetailQueryVariables
  >['refetch'];
};
const AppInner: React.FC<AppInnerProps> = ({
  fetchMore,
  queryRef,
  refetch,
}) => {
  const { isLoggedIn } = useLoginState();
  const { data, error } = useReadQuery(queryRef);
  const [isFilterPending, startFilterTransition] = useTransition();
  const [isPaginationPending, startPaginationTransition] = useTransition();
  const [offset, setOffset] = useState<number>(LIST_LIMIT);
  const [activeFilters, setActiveFilters] = useState<ActiveFiltersState>(
    DEFAULT_ACTIVE_FILTERS
  );

  const companyId = useEntityPageStore((state) => state.pageContext.companyId)!;
  const isResultsRefreshing = isFilterPending && offset === LIST_LIMIT;

  const handlePagination = () => {
    if (isResultsRefreshing) {
      return;
    }

    const paginationOffset = offset;

    startPaginationTransition(() => {
      void fetchMore({
        variables: {
          ...buildJobsDetailVariables({
            companyId,
            isLoggedIn,
            keywords: activeFilters.keywords,
            selectedItems: activeFilters.selectedItems,
            offset: paginationOffset,
          }),
        },
        updateQuery: (
          prev: JobsDetailQuery,
          { fetchMoreResult }: { fetchMoreResult: JobsDetailQuery }
        ) => {
          return mergeJobsDetailResults(prev, fetchMoreResult);
        },
      }).then(() => {
        setOffset((currentOffset: number) => currentOffset + LIST_LIMIT);
      });
    });
  };

  const handleFilterChange = (
    selectedItems: FilterStateSelectedItemsType,
    keywords: string
  ) => {
    setActiveFilters({
      keywords,
      selectedItems,
    });
    setOffset(LIST_LIMIT);

    startFilterTransition(() => {
      void refetch(
        buildJobsDetailVariables({
          companyId: companyId ?? '',
          isLoggedIn,
          keywords,
          selectedItems,
        })
      );
    });
  };

  const aggregations = buildJobsFilterAggregations(
    data?.jobSearchByQuery?.aggregations
  );

  if (error || isError(data)) {
    return <ErrorFallback onReload={() => void refetch()} />;
  }

  return (
    <>
      {isLoggedIn ? (
        <JobsFilter
          onFiltersChanges={handleFilterChange}
          aggregations={aggregations}
        />
      ) : null}
      {isResultsRefreshing ? (
        <div data-testid="jobs-refreshing-indicator">
          <JobsListItem.Skeleton />
        </div>
      ) : (
        <JobsDetail
          onLazyLoad={handlePagination}
          boundedTotal={data?.jobSearchByQuery?.boundedTotal}
          searchQuery={data?.jobSearchByQuery?.searchQuery}
          paginationLoading={isPaginationPending || isResultsRefreshing}
          jobResults={data?.jobSearchByQuery?.collection}
        />
      )}
    </>
  );
};

const Fallback: React.FC = () => {
  return <JobsDetailSkeleton />;
};

const ErrorFallback: React.FC<{
  onReload?: () => void;
}> = ({ onReload = () => window.location.reload() }) => {
  return (
    <div data-testid={'errorContainer'}>
      <WarningMessageBox
        headerText="EP_ERROR_HEADER"
        bodyText="EP_ERROR_BODY"
        buttonText="EP_ERROR_RELOAD_CTA"
        onClick={onReload}
      />
    </div>
  );
};

export const App: React.FC = () => {
  const { isLoggedIn } = useLoginState();
  const companyId = useEntityPageStore((state) => state.pageContext.companyId)!;

  const initialVariables = useMemo(() => {
    return buildJobsDetailVariables({
      companyId,
      isLoggedIn,
    });
  }, [companyId, isLoggedIn]);

  const [queryRef, { refetch, fetchMore }] = useBackgroundQuery(
    JobsDetailDocument,
    {
      variables: initialVariables,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  return (
    <ErrorBoundary onError={() => <ErrorFallback />}>
      <Suspense fallback={<Fallback />}>
        <AppInner fetchMore={fetchMore} queryRef={queryRef} refetch={refetch} />
      </Suspense>
    </ErrorBoundary>
  );
};
